import React, { useCallback, useEffect, useRef, useState } from "react";
import ReserveButton from "../registration/ReserveButton";
import { ArrowIcon, Marigold, PauseIcon, PlayIcon } from "../art/Icons";
import { EVENT } from "../../config/event";
import { TITLE_TYPING, typingSchedule } from "../../lib/intro";
import portraitUrl from "../../assets/la-muerte.png";

// After the home-page intro the title is typed a character at a time (styles/intro.css); the marigold spins in
// right after "La".
const [LA_TYPING, MUERTE_TYPING] = typingSchedule(["La", "Muerte"]);
const MARIGOLD_SPIN_AT = LA_TYPING[LA_TYPING.length - 1].at + TITLE_TYPING.step;

/** One word of the title as characters the intro can reveal; the heading's aria-label names the whole title. */
const TypedWord = ({ chars }) => (
  <span aria-hidden="true">
    {chars.map(({ char, at, cursorFor }) => (
      <span
        key={at}
        className={cursorFor === null ? "type-char type-char--last" : "type-char"}
        style={{ "--type-at": `${at}ms`, "--cursor-for": `${cursorFor ?? 0}ms` }}
      >
        {char}
      </span>
    ))}
  </span>
);

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Three.js is only fetched where it can run; everywhere else the still portrait stays.
const supportsWebGL2 = () => typeof window.WebGL2RenderingContext !== "undefined";

/**
 * The original La Muerte event identity with the supplied, lazy-loaded animated portrait.
 * The still image remains visible until WebGL is ready, or when animation is unavailable.
 */
const CandlelitHero = ({ isOpen }) => {
  const rootRef = useRef(null);
  const stageRef = useRef(null);
  const sceneRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [isPaused, setIsPaused] = useState(prefersReducedMotion);
  const [status, setStatus] = useState("");

  useEffect(() => {
    if (!supportsWebGL2()) return undefined;
    let isCancelled = false;
    import("./portraitScene")
      .then(({ createPortraitScene }) => {
        if (isCancelled) return null;
        return createPortraitScene({
          root: rootRef.current,
          stage: stageRef.current,
          artUrl: portraitUrl,
          startPaused: prefersReducedMotion(),
          onPausedChange: setIsPaused,
        });
      })
      .then((scene) => {
        if (!scene) return;
        if (isCancelled) {
          scene.destroy();
          return;
        }
        sceneRef.current = scene;
        setIsReady(true);
      })
      .catch((error) => {
        console.error("Animated portrait unavailable, showing the still image:", error);
      });
    return () => {
      isCancelled = true;
      sceneRef.current?.destroy();
      sceneRef.current = null;
    };
  }, []);

  const togglePause = useCallback(() => {
    const next = !isPaused;
    sceneRef.current?.setPaused(next);
    setStatus(next ? "Animace pozastavena." : "Animace běží.");
  }, [isPaused]);

  const scatterPetals = useCallback(() => {
    sceneRef.current?.scatter();
    setStatus("Lístky rozsypány.");
  }, []);

  const heroClass = ["candle-hero", isReady && "ready", isPaused && "is-paused"].filter(Boolean).join(" ");

  return (
    <section ref={rootRef} className={heroClass} aria-labelledby="hero-heading">
      <div
        ref={stageRef}
        className="candle-stage"
        role="img"
        aria-label="La Muerte se ve světle svíček jemně pohupuje a kolem ní poletují zlaté lístky."
      >
        <img className="candle-fallback" src={portraitUrl} alt="" width="1139" height="1381" fetchpriority="high" draggable="false" />
      </div>
      <div className="candle-title">
        <p className="eyebrow">
          <span aria-hidden="true">✦</span>
          Día de los Muertos na FAI
        </p>
        <p className="hero-invitation">Fakulta mrtvých vás zve</p>
        <h1 id="hero-heading" aria-label="La Muerte">
          <span className="hero-la">
            <TypedWord chars={LA_TYPING} />
            <Marigold style={{ "--spin-at": `${MARIGOLD_SPIN_AT}ms` }} />
          </span>{" "}
          <em>
            <TypedWord chars={MUERTE_TYPING} />
          </em>
        </h1>
        <p className="candle-subtitle">¡Viva la vida, viva los muertos!</p>
        <p className="hero-description">
          Vstup do <b>Země vzpomínek</b>. Na jeden den ožijí příběhy, rozsvítí se svíčky
          a sama La Muerte tě provede na druhou stranu.
        </p>
        <div className="hero-cta">
          <ReserveButton isOpen={isOpen} className="btn btn-marigold">
            Rezervovat místo <ArrowIcon />
          </ReserveButton>
          <a className="btn btn-ghost" href="#story">
            Co vás čeká <span aria-hidden="true">↓</span>
          </a>
        </div>
        <p className="hero-date">{EVENT.date}<span aria-hidden="true"> / </span>{EVENT.time}<span aria-hidden="true"> / </span>Zlín</p>
      </div>

      <p className="candle-side" aria-hidden="true">
        ENTRE LA VIDA Y LA MUERTE
      </p>

      <div className="candle-footer">
        <p className="candle-hint">
          <Marigold />
          <span className="hint-desktop">
            I vzpomínky mohou ožít.<br /><strong>Pohni myší. Rozsyp lístky.</strong>
          </span>
          <span className="hint-touch">Dotkni se a rozviř lístky.</span>
        </p>
        <div className="candle-controls">
          <button
            type="button"
            className="icon-btn"
            onClick={togglePause}
            disabled={!isReady}
            aria-pressed={isPaused}
            aria-label={isPaused ? "Spustit animaci" : "Pozastavit animaci"}
          >
            {isPaused ? <PlayIcon /> : <PauseIcon />}
          </button>
          <span className="control-divider" aria-hidden="true" />
          <button type="button" className="bloom-button" onClick={scatterPetals} disabled={!isReady}>
            <span aria-hidden="true">✦</span>
            Rozsypat lístky
          </button>
        </div>
      </div>

      <p className="sr" aria-live="polite">
        {status}
      </p>
    </section>
  );
};

export default CandlelitHero;
